import { AbpAuthResponse, AuthService, ConfigStateService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { signal } from '@angular/core'; // Angular 16+
import { Router } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';
import { OAuthService } from 'angular-oauth2-oidc';
import { IdentityUserDto, IdentityUserService, } from '../proxy/volo/abp/identity';
import { environment } from 'src/environments/environment';
import { ErrorService } from './error.service';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AppConstants } from '../app.constants';
import { RegisterDtoExt } from '../proxy/skybound/sky-gen1/account';
import { AccountAppServiceExService, IdentityUserAppServiceExService, PasswordResetTokenDto } from '../proxy/skybound/sky-gen1/identity';
import { Observable, timeout } from 'rxjs';
import { NativeService } from './native.service';
import { BiometryType } from '@capgo/capacitor-native-biometric';
import { StorageService } from './storage.service';
import { BiometricService } from './biometric.service';
import { PharmacyFacadeService } from './pharmacy-facade.service';

const AuthErrorCodes = {
	REQUIRES_CONFIRM_USER: 'RequiresConfirmUser'
} as const;

type LoginResult = 'ok' | 'requires-otp' | 'failed' | 'invalid_grant';

@Injectable({
	providedIn: 'root'
})
export class AuthFacadeService
{

	public isLoggedIn = signal<boolean>(false);
	public username = signal<string | null>(null);
	private abpAuthResponse: AbpAuthResponse | null = null;

	constructor(
		private navController: NavController,
		private authService: AuthService,
		private accountAppServiceExService: AccountAppServiceExService,
		private identityUserAppServiceExService: IdentityUserAppServiceExService,
		private oAuthService: OAuthService,
		private errorService: ErrorService,
		private nativeService: NativeService,
		private storageService: StorageService,
		private biometricService: BiometricService,
		private pharmacyFacadeService: PharmacyFacadeService
	)
	{

	}

	async hasLoggedIn(redirectIfLoggedOut: boolean = true): Promise<boolean>
	{
		try
		{
			console.debug('AuthFacadeService: hasLoggedIn called with redirectIfLoggedOut =', redirectIfLoggedOut);

			// call {environment.apis.default.url}/health and return false if not reachable
			const backendAvailable = await this.checkBackendAvailability();
			if (!backendAvailable)
			{
				console.warn('Backend is not reachable, treating as logged out without redirect');
				return false;
			}

			console.debug('AuthFacadeService: hasLoggedIn called, checking token validity');
			let hasValidAccessToken = this.oAuthService.hasValidAccessToken();
			console.debug('Has valid access token:', hasValidAccessToken);

			if (!hasValidAccessToken && this.oAuthService.getRefreshToken())
			{
				// Try to refresh the access token
				try
				{
					console.debug('Attempting to refresh access token');
					await this.oAuthService.refreshToken();
					hasValidAccessToken = this.oAuthService.hasValidAccessToken();
					console.debug('Access token refreshed:', hasValidAccessToken);
				} catch (err)
				{
					console.error('Refresh token failed:', err);
				}
			}

			this.isLoggedIn.set(hasValidAccessToken);

			if (!hasValidAccessToken)
			{
				if (!redirectIfLoggedOut)
				{
					console.debug('No valid access token, but redirect is disabled. Returning false without logout.');
					return hasValidAccessToken;
				}
				// Only logout if the API is reachable and not a network error
				if (navigator.onLine) 
				{
					console.debug('No valid access token, performing logout to clear session');
					console.debug('Clearing current pharmacy profile');
					this.storageService.setItem(AppConstants.storage_lastPharmacyLinkProfile, null);
					this.logout();
				} else
				{
					// Show a toast or error, but do not redirect
					this.errorService.handleError('Network error: Unable to reach authentication server. Please check your connection.', 'toast');
				}
			}

			console.debug('AuthFacadeService: hasLoggedIn returning', hasValidAccessToken);
			return hasValidAccessToken;
		} catch (error)
		{
			console.error('Error checking login status:', error);
			return false;
		}
	}

	async checkBackendAvailability(): Promise<boolean>
	{
		try {
			const baseUrl = environment.apis.default.url.replace(/\/+$/, '');
			const response = await fetch(`${baseUrl}/health`, { method: 'GET', cache: 'no-cache' });
			return response.ok;
		}
		catch (error) {
			console.error('Error checking backend availability:', error);
			return false;
		}
	}

	public async loginWithRefreshToken(refreshToken: string): Promise<LoginResult>
	{
		console.debug('AuthService: loginWithRefreshToken called');

		const grantType = 'refresh_token';
		let params = {
			refresh_token: refreshToken
		};

		let headers = new HttpHeaders({
			'__tenant': environment['tenant'],
			'Content-Type': 'application/json'
		});

		try
		{
			const result = await this.authService.loginUsingGrant(grantType, params, headers);
			console.log('Login with refresh token successful:', result);
			this.abpAuthResponse = result;
			this.saveBiometricCredentialsInMemory();
			this.isLoggedIn.set(true);
			return 'ok';
		}
		catch (error)
		{
			if (error instanceof HttpErrorResponse && error.error)
			{
				if (error.error.error == 'invalid_grant')
				{
					return 'invalid_grant';
				}
			}
			console.error('Login with refresh token error:', error);
			this.errorService.handleError(error, 'toast', 'Biometric login failed');
			return 'failed';
		}
	}

	public async login(username: string, password: string, otp: string = ''): Promise<LoginResult>
	{
		console.debug('AuthService: login called');

		const grantType = 'password';
		let params = {
			username,
			password
			// TwoFactorProvider: twoFactor.provider,
			// TwoFactorCode: twoFactor.code,
		};

		let headers = new HttpHeaders({
			'__tenant': environment['tenant'],
			'Content-Type': 'application/json'
		});

		try
		{
			const result = await this.authService.loginUsingGrant(grantType, params, headers);
			console.log('Login successful:', result);
			this.isLoggedIn.set(true);
			this.username.set(username);
			localStorage.setItem(AppConstants.storage_username, username);
			this.abpAuthResponse = result;
			return 'ok';
		}
		catch (error)
		{
			if (error instanceof HttpErrorResponse)
			{
				if (error.status === 400)
				{
					if (error.error.error == 'invalid_grant' && error.error.error_description == AuthErrorCodes.REQUIRES_CONFIRM_USER)
					{
						return 'requires-otp';
					}
				}
			}
			console.error('Login error:', error);
			this.errorService.handleError(error);
			return 'failed';
		}
	}

	public async logout()
	{
		console.debug('AuthService: logout called, checking for soft logout');
		const biometricsEnabled = await this.nativeService.isNative()
			? await this.storageService.getItem(AppConstants.storage_biometricLoginEnabled)
			: false;

		this.oAuthService.logOut(true);
		this.isLoggedIn.set(false);
		
		// Clear the current pharmacy profile to update menu state
		this.pharmacyFacadeService.setCurrentPharmacyProfile(null, false);
		
		if (biometricsEnabled)
		{
			this.navController.navigateRoot(['/auth/login'], { queryParams: { logout: true } });
		}
		else
		{
			this.navController.navigateRoot(['/splash']);
		}
	}

	public register(mobile: string, password: string, firstName?: string, lastName?: string, email?: string): Observable<IdentityUserDto | null>
	{
		console.debug('AuthService: register called');

		const emailUri = environment['defaultCustomerEmailDomain'];
		const emailAddress = email ?? `${mobile}@${emailUri}`;
		let params = {
			userName: mobile,
			password,
			firstName,
			lastName,
			emailAddress,
			appName: AppConstants.appName
		} as RegisterDtoExt;

		return this.accountAppServiceExService.register(params, {});

	}

	public sendPhoneConfirmCode(username: string): Observable<void>
	{
		return this.identityUserAppServiceExService.sendConfirmPhoneCode(username, {});
	}

	public confirmPhone(username: string, code: string): Promise<boolean>
	{
		return new Promise<boolean>((resolve) =>
		{
			this.identityUserAppServiceExService.confirmPhone(username, code, {})
				.pipe(timeout(AppConstants.defaultTimeout))
				.subscribe({
					next: (res) =>
					{
						console.debug('Phone confirmation successful:', res);
						resolve(true);
					},
					error: (error) =>
					{
						if (error instanceof HttpErrorResponse)
						{
							if (error.status === 403)
							{
								if (error.error.error == 'invalid_grant')
								{
									resolve(false);
									return;
								}
							}
						}
						console.error('Phone confirmation error:', error);
						resolve(false);
					}
				});
		});
	}

	public sendMobilePasswordResetCode(username: string): Observable<void>
	{
		return this.identityUserAppServiceExService.sendMobilePasswordResetCode(username, {});
	}

	public confirmMobilePasswordResetRequest(username: string, otp: string): Observable<PasswordResetTokenDto>
	{
		return this.identityUserAppServiceExService.confirmMobilePasswordResetRequest(username, otp, {});
	}

	public resetPassword(userId: string, resetToken: string, password: string): Observable<void>
	{
		var request = {
			userId,
			resetToken,
			password
		};
		return this.accountAppServiceExService.resetPassword(request, {});
	}

	//saves token from latest login and the
	async saveBiometricCredentialsAfterLogin(): Promise<void>
	{
		const abpAuthResponse = this.abpAuthResponse;
		const expires_in = abpAuthResponse!.expires_in;
		const refresh_token = abpAuthResponse!.refresh_token!;
		await this.saveBiometricCredentialsWithTokenAndExpiry(expires_in, refresh_token);
	}

	async saveFallbackBiometricCredentials(username: string, password: string): Promise<void>
	{
		const server = this.getFallbackServerForBiometrics();
		await this.biometricService.setUserCredentialsWithBiometrics(username, password, server);
		console.debug('Biometric credentials saved after login with username and password');
	}

	public getFallbackServerForBiometrics(): string
	{
		return 'fallback-' + this.getServerForBiometrics();
	}

	//saves the token that is in memory
	async saveBiometricCredentialsInMemory(): Promise<void>
	{
		const token = this.token;
		const expiry = this.expiry;
		if (token && expiry)
		{
			this.token = token;
			this.expiry = expiry;
			await this.saveBiometricCredentialsWithTokenAndExpiry(expiry, token);
			console.debug('Biometric credentials saved in memory with expiry:', new Date(expiry));
		}
		else
		{
			console.debug('No token or expiry available to save in memory');
		}
	}

	async saveBiometricCredentialsWithTokenAndExpiry(expires_in: number, refresh_token: string): Promise<void>
	{
		const expiryDate = expires_in ? new Date(Date.now() + expires_in * 1000) : null;
		const refreshToken = refresh_token;
		const server = this.getServerForBiometrics();
		await this.biometricService.setUserCredentialsWithBiometrics(expiryDate?.toISOString() ?? '', refreshToken, server);
		console.debug('Biometric credentials saved with expiry:', expiryDate);
	}

	public getServerForBiometrics(): string
	{
		return environment.application.baseUrl ?? 'healthmax.co.nz';
	}

	public async subscribeToTokenEvents(): Promise<void>
	{
		const isNative = await this.nativeService.isNative();
		if (!isNative)
		{
			return; // Only handle biometric credentials on native platforms
		}

		const biometricsEnabled = await this.storageService.getItem(AppConstants.storage_biometricLoginEnabled);
		if (!biometricsEnabled)
		{
			return; // Only handle refresh credentials if biometrics is enabled
		}

		// Subscribe to OAuthService events to handle refresh token changes.
		this.oAuthService.events.subscribe(async event =>
		{
			console.debug('OAuth event received:', event.type);

			if (event.type === 'token_received' || event.type === 'token_refreshed')
			{
				const token = await this.oAuthService.getAccessToken();
				const expiry = await this.oAuthService.getAccessTokenExpiration();
				console.debug('Token event - access token:', token, 'expiry:', expiry);

				if (!token || !expiry)
				{
					console.debug('Token or expiry is missing, skipping credential save');
					return;
				}

				//store in private variable for use in biometric login when we toggle in settings
				this.token = token;
				this.expiry = expiry;

				// Check if biometrics is enabled before attempting to save credentials
				const biometricsEnabled = await this.nativeService.isNative()
					? await this.storageService.getItem(AppConstants.storage_biometricLoginEnabled)
					: false;

				if (!biometricsEnabled)
				{
					console.debug('Biometrics not enabled, skipping credential save');
					return;
				}

				// Save biometric credentials when a new refresh token is received
				this.saveBiometricCredentialsWithTokenAndExpiry(expiry, token);
			}
			if (event.type === 'token_error')
			{
				console.warn('Token refresh failed');

				const error = event as any;

				if (error?.reason?.status === 0)
				{
					console.warn('Network issue during token refresh - staying offline');
					return;
				}
			}
		});
	}

	private token: string | null = null;
	private expiry: number | null = null;
}
